import React, { useCallback, useMemo, useRef, useState } from "react";

import ShipeeziGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { Color } from "../../../../../../components/Form/Button";
import { DespatchTruck } from "../../../../../../models/despatch";
import { SearchAttribute } from "../../../../../../models/search.attribute";
import { Searches } from "../../../../../../models/shipeezi.table";
import { formatDateWithoutTimeZone } from "../../../../../../util/Format";
import { paginate } from "../../../../../../util/paginate";
import { DespatchEmptySection } from "../../components/EmptySection";
import { PlusIcon } from "../../styles";
import useFilterTruck from "../hooks/useFilterTruck";
import TruckMaintenance, { TruckMaintenanceHandles } from "./Maintenance";
import { MainDiv, StyledButton } from "./styles";

const noTruckIcon = require("Assets/img/empty-state-grid-truck.svg");

interface Props {
  truck: DespatchTruck[];
  loading: boolean;
  viewOnly?: boolean;
  onUpdateItems: (data: DespatchTruck[]) => void;
}

const DespatchTruckTab = (props: Props) => {
  const { truck, loading, onUpdateItems } = props;

  const { t: translate } = useTranslation();
  const maintenanceRef = useRef<TruckMaintenanceHandles>(null);

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const filteredData = useMemo(() => {
    let data = truck;

    if (searchAttributes.length > 0) {
      data = useFilterTruck(truck, searchAttributes);
    }

    return paginate(data, page > 0 ? page - 1 : 0, pageSize);
  }, [truck, searchAttributes, page, pageSize]);

  const renderAddTruck = useCallback(() => {
    return (
      <StyledButton customColor={Color.SECONDARY} onClick={() => maintenanceRef.current?.open()}>
        <PlusIcon color="#ffffff" size={18} />
        {translate("addTruck")}
      </StyledButton>
    );
  }, []);

  const onOpenEditTruck = (row: DespatchTruck) => {
    maintenanceRef.current?.open(row);
  };
  const onSubmitTruckMaintenance = (data: DespatchTruck) => {
    const success = data?.id || data.uuid ? onEditTruck(data) : onCreateTruck(data);

    if (success) maintenanceRef.current?.close();
  };

  const onEditTruck = (row: DespatchTruck) => {
    const index = truck.findIndex((data) => data.id === row.id || data.uuid === row.uuid);

    if (index < 0) return;

    const cloned = _.cloneDeep(truck);

    cloned.splice(index, 1, row);
    onUpdateItems(cloned);

    return true;
  };

  const onCreateTruck = (row: DespatchTruck) => {
    onUpdateItems([{ ...row, uuid: uuid() }, ...truck]);

    return true;
  };

  const onRemoveTruck = (row: DespatchTruck) => {
    const index = truck.findIndex(
      (data) => (data.id === row.id && data.id) || (data.uuid === row.uuid && data.uuid)
    );

    if (index < 0) return;

    const cloned = _.cloneDeep(truck);

    cloned.splice(index, 1);
    onUpdateItems(cloned);
  };

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
  };
  const renderDeliveryHour = (value?: string) => {
    if (!value) return "";

    const date = new Date();
    const [hour, minutes] = value.split(":");

    date.setHours(Number(hour));
    date.setMinutes(Number(minutes));

    return format(date, "HH:mm a");
  };
  const columns: Column[] = [
    {
      name: "fleetType",
      title: translate("truckType"),
      hideSort: true,
    },
    {
      name: "bookingNumber",
      title: translate("truckBookingNumber"),
      hideSort: true,
    },
    {
      name: "palletsPerFleet",
      title: translate("palletsPerTrucks"),
      hideSort: true,
    },
    {
      name: "deliveryDate",
      title: translate("deliveryDate"),
      formatText: (value?: string) => formatDateWithoutTimeZone(value),
      hideSort: true,
    },
    {
      name: "deliveryWindowOpen",
      title: translate("deliveryWindowOpen"),
      formatText: renderDeliveryHour,
      hideSort: true,
    },
    {
      name: "deliveryWindowClose",
      title: translate("deliveryWindowClose"),
      formatText: renderDeliveryHour,
      hideSort: true,
    },
  ];
  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch"],
      right: ["onAddTruck"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    onAddTruck: renderAddTruck(),
  };

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const renderScreen = () => {
    if (!truck || !truck.length) {
      return (
        <DespatchEmptySection
          buttonLabel={translate("addTruck")}
          descriptionScreen={translate("truck").toLocaleLowerCase()}
          onAdd={() => maintenanceRef.current?.open()}
          maintenanceScreen
          icon={noTruckIcon}
        />
      );
    }

    return (
      <MainDiv data-cy="despatch-truck-container">
        <ShipeeziGrid
          data={filteredData}
          columns={columns}
          totalItems={truck.length}
          loadingData={loading}
          emptyData={translate("noInformationToBeShown")}
          currentPage={page}
          onNextPage={setPage}
          onPreviousPage={setPage}
          onGoToPage={setPage}
          pageSize={pageSize}
          onChangePageSize={setPageSize}
          pageSizeList={[20, 40, 60, 100]}
          generalActions={generalActions}
          positionActionsColumn="right"
          rowActions={{
            handleEdit: (_, row) => onOpenEditTruck(row),
            handleDelete: (_, row) => onRemoveTruck(row),
          }}
        />
      </MainDiv>
    );
  };

  return (
    <React.Fragment>
      <TruckMaintenance ref={maintenanceRef} onSubmit={onSubmitTruckMaintenance} />

      {renderScreen()}
    </React.Fragment>
  );
};

export default DespatchTruckTab;
